import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useMemo } from "react";
import {
	type BillingAddonSlug,
	BillingPlanSlug,
	type BillingUpgradeRequestDto,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useSubscription = () => {
	const { id: companyId } = useCompany();
	const subscriptions = useSuspenseQuery({
		queryKey: QueryKeys.Billing.Status(companyId),
		queryFn: () => Api.GetBillingStatus().then((res) => res.data),
	});

	return { subscriptions };
};

export const useBillingEntitlements = () => {
	const { id: companyId } = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Billing.Entitlements(companyId),
		queryFn: () => Api.GetBillingEntitlements().then((res) => res.data),
	});
};

export const useBillingPlanPaidCheck = () => {
	const { subscriptions } = useSubscription();

	return useMemo(
		() =>
			subscriptions.data.reduce(
				(curr, last) => last.plan.slug !== BillingPlanSlug.FREE || curr,
				false,
			),
		[subscriptions],
	);
};

export const useInvoices = () => {
	const { id: companyId } = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Billing.Invoices(companyId),
		queryFn: () => Api.GetInvoices().then((res) => res.data),
	});
};

export const usePlans = () => {
	const { id: companyId } = useCompany();

	const plans = useSuspenseQuery({
		queryKey: QueryKeys.Billing.Prices(companyId),
		queryFn: () => Api.GetPlans().then((res) => res.data),
	});

	return { plans: plans.data };
};

export const useBillingActions = () => {
	const { id: companyId } = useCompany();
	const emit = useAlertEmit();
	const queryClient = useQueryClient();

	const invalidateBilling = () => {
		setTimeout(() => {
			queryClient.refetchQueries({
				queryKey: QueryKeys.Billing.Status(companyId),
			});
			queryClient.refetchQueries({
				queryKey: QueryKeys.Billing.Entitlements(companyId),
			});
			queryClient.refetchQueries({
				queryKey: QueryKeys.Billing.Prices(companyId),
			});
		}, 3000);
	};

	const upgrade = useMutation({
		mutationFn: (dto: BillingUpgradeRequestDto) =>
			Api.UpgradeSubscription(dto).then((res) => res.data),
		onSuccess: async (response) => {
			if (response.redirectUrl) {
				window.location.href = response.redirectUrl;
			} else {
				emit({
					type: AlertType.SUCCESS,
					content: response.message,
				});
				invalidateBilling();
			}
		},
		onError: (e: Error) => {
			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) && e.response?.data?.message) ||
					"Failed to upgrade subscription",
			});
		},
	});

	const addAddon = useMutation({
		mutationFn: (addon: BillingAddonSlug) =>
			Api.AddBillingAddon({ addon }).then((res) => res.data),
		onSuccess: async (response) => {
			if (response.redirectUrl) {
				window.location.href = response.redirectUrl;
				return;
			}
			emit({
				type: AlertType.SUCCESS,
				content: response.message ?? "Add-on activated",
			});
			invalidateBilling();
		},
		onError: (e: Error) => {
			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) && e.response?.data?.message) ||
					"Failed to activate add-on",
			});
		},
	});

	const removeAddon = useMutation({
		mutationFn: (addon: BillingAddonSlug) =>
			Api.RemoveBillingAddon({ addon }).then((res) => res.data),
		onSuccess: async (response) => {
			emit({
				type: AlertType.SUCCESS,
				content: response.message ?? "Add-on removed",
			});
			invalidateBilling();
		},
		onError: (e: Error) => {
			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) && e.response?.data?.message) ||
					"Failed to remove add-on",
			});
		},
	});

	const cancel = useMutation({
		mutationFn: () => Api.CancelSubscription().then((res) => res.data),
		onSuccess: async () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Your subscription has been cancelled",
			});
			invalidateBilling();
		},
		onError: (e: Error) => {
			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) && e.response?.data?.message) ||
					"Failed to cancel subscription",
			});
		},
	});

	const openStripePortal = useMutation({
		mutationFn: () => Api.GetStripeCustomerPortal().then((res) => res.data),
		onSuccess: async (response) => {
			window.location.href = response.redirectUrl;
		},
		onError: (e: Error) => {
			emit({
				type: AlertType.ERROR,
				content:
					(isAxiosError(e) ? e.response?.data?.message : undefined) ||
					"Customer portal is currently unavailable",
			});
		},
	});

	return {
		upgrade,
		cancel,
		openStripePortal,
		addAddon,
		removeAddon,
	};
};
